import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, Download, ChevronDown, Flame } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { ledgerRecords } from '@/data/mockData';
import GradeTooltip from '@/components/GradeTooltip';

const RiskBadge = ({ tier }) => {
  const cls = tier === 'HIGH' ? 'risk-high' : tier === 'MEDIUM' ? 'risk-medium' : 'risk-low';
  return <span className={`${cls} text-[10px] font-mono px-2 py-0.5 rounded-full inline-block`}>{tier}</span>;
};

const SigIcon = ({ status }) => {
  if (status === 'verified') return <span className="text-[#10B981] text-xs" title="Verified">&#10003;</span>;
  if (status === 'warning') return <span className="text-[#F59E0B] text-xs" title="Gap Detected">&#9888;</span>;
  if (status === 'closed') return <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20" title="Closed">CLOSED</span>;
  return <span className="text-[#EF4444] text-xs" title="Non-Compliant">&#9679;</span>;
};

export default function DecisionLedger() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [expandedChain, setExpandedChain] = useState(null);
  const [temporalQuery, setTemporalQuery] = useState('Show me every HIGH-RISK decision made while evidence coverage was below 80%');
  const [queryResult, setQueryResult] = useState(null);
  const [queryHistory, setQueryHistory] = useState([]);

  const runQuery = () => {
    let result = null;
    if (temporalQuery.includes('coverage') && !temporalQuery.includes('Control State') && !temporalQuery.includes('oversight')) result = 'coverage';
    else if (temporalQuery.includes('LA-0912')) result = 'reviewer';
    else if (temporalQuery.includes('Control State') || (temporalQuery.includes('CSS') && !temporalQuery.includes('oversight'))) result = 'cssgap';
    else if (temporalQuery.includes('oversight') && temporalQuery.includes('literacy')) result = 'cssoer';
    else if (temporalQuery.includes('model version') || temporalQuery.includes('Change Control')) result = 'unauthorized';
    else if (temporalQuery.includes('AUDIT-GRADE') && temporalQuery.includes('exceeded')) result = 'exemplary';
    else if (temporalQuery.includes('degraded') && temporalQuery.includes('defensibility')) result = 'compound_full';
    setQueryResult(result);
    if (result) {
      const counts = { coverage: 4, reviewer: 2, cssgap: 4, cssoer: 3, unauthorized: 1, exemplary: 847, compound_full: 3 };
      setQueryHistory(prev => [{ q: temporalQuery, count: counts[result] || 0 }, ...prev].slice(0, 3));
    }
  };

  const dimCount = { coverage: 1, reviewer: 1, cssgap: 1, cssoer: 2, unauthorized: 1, exemplary: 1, compound_full: 3 };

  const filtered = ledgerRecords.filter(
    (r) => r.id.toLowerCase().includes(search.toLowerCase()) || r.model.toLowerCase().includes(search.toLowerCase()) || r.workflow.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="decision-ledger-page" className="space-y-4">
      {/* Top controls */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
      {/* Temporal Query */}
      <div className="glass-card p-4" data-testid="temporal-query">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[9px] text-[#6366F1] font-mono">Temporal governance query · Proof Graph Service</span>
            <input
              type="text"
              value={temporalQuery}
              onChange={(e) => setTemporalQuery(e.target.value)}
              className="w-full pl-[310px] pr-4 py-2.5 bg-[#080D1A] border border-[#6366F1]/20 rounded-lg text-xs text-[#F8FAFC] placeholder:text-[#475569] focus:outline-none focus:border-[#6366F1] transition-colors"
            />
          </div>
          <button
            data-testid="run-temporal-query"
            onClick={runQuery}
            className="bg-[#6366F1] hover:bg-[#818cf8] text-white text-xs font-medium px-4 py-2.5 rounded-lg transition-all flex-shrink-0"
          >
            Run Query
          </button>
        </div>
        {/* Dimension indicator */}
        {queryResult && <div className="mt-1 text-[9px] font-mono text-[#475569]">Dimensions: <span className={dimCount[queryResult] >= 3 ? 'text-[#6366F1] font-semibold' : dimCount[queryResult] >= 2 ? 'text-[#F59E0B]' : 'text-[#64748B]'}>{dimCount[queryResult]}</span></div>}
        {queryResult && (
          <div className="mt-3 space-y-2">
            <div className="text-[10px] text-[#10B981] font-mono">
              {queryResult === 'coverage' && '4 records matched · April 15, 2026 · 11:14–14:31 UTC'}
              {queryResult === 'reviewer' && '2 records matched · April 15–17, 2026 · Reviewer: LA-0912'}
              {queryResult === 'cssgap' && '4 records matched · April 15, 2026 · CSS gap window: 3h 17m'}
              {queryResult === 'cssoer' && '3 records matched · April 15, 2026 · Oversight + CSS compound query'}
              {queryResult === 'unauthorized' && '1 record matched · April 14, 2026 · 16:33 UTC'}
              {queryResult === 'exemplary' && '847 records matched · All workflows · Strong oversight signals'}
              {queryResult === 'compound_full' && '3 records matched · Compound governance failure'}
            </div>
            <div className="glass-card p-4 space-y-1.5" data-testid="temporal-query-result">
              <div className="text-[9px] font-semibold text-[#475569] uppercase tracking-wider">Temporal Query Result</div>
              <div className="text-[10px] text-[#64748B]">Query: <span className="text-[#94A3B8] italic">"{temporalQuery}"</span></div>
              <div className="text-[10px] text-[#64748B]">Executed against: <span className="text-[#94A3B8]">Evidence fabric · 47,832 records · 5 workflows</span></div>
              <div className="text-[10px] text-[#64748B]">Reconstruction: <span className="text-[#818CF8] font-mono">Proof Graph Service</span></div>

              {/* Per-query result fields */}
              {queryResult === 'cssoer' && (
                <div className="mt-1 pt-1 border-t border-[#1E2A45] space-y-0.5">
                  <div className="text-[10px] text-[#64748B]">Dimensions queried: <span className="text-[#F59E0B]">Oversight Evidence (OER) + Control State (CSS)</span></div>
                  <div className="text-[10px] text-[#64748B]">Records matched: <span className="text-[#F8FAFC]">3 decisions · April 15, 2026 · 11:14–13:47 UTC</span></div>
                  <div className="text-[10px] text-[#64748B]">Oversight threshold: <span className="text-[#F59E0B]">23 seconds review duration — below defensibility</span></div>
                  <div className="text-[10px] text-[#64748B]">Certification status: <span className="text-[#F59E0B]">LA-0912 · AI Literacy certification expired Mar 2026</span></div>
                  <div className="text-[10px] text-[#64748B]">CSS gap active: <span className="text-[#F59E0B]">Yes · Same window · External Legal Corpus</span></div>
                  <div className="text-[10px] text-[#64748B]">Compound governance gap: <span className="text-[#F59E0B] font-semibold">Control gap + oversight gap simultaneously active</span></div>
                </div>
              )}
              {queryResult === 'unauthorized' && (
                <div className="mt-1 pt-1 border-t border-[#1E2A45] space-y-0.5">
                  <div className="text-[10px] text-[#64748B]">Change detected: <span className="text-[#F8FAFC]">GPT-4o Mini · parameter change · production workflow</span></div>
                  <div className="text-[10px] text-[#64748B]">Change Control record: <span className="text-[#EF4444]">CHG-2026-0086 · Status: UNAUTHORIZED</span></div>
                  <div className="text-[10px] text-[#64748B]">Human approval: <span className="text-[#EF4444]">None recorded</span></div>
                  <div className="text-[10px] text-[#64748B]">Blast radius: <span className="text-[#F8FAFC]">Claims Document Processing · 8,203 decisions in scope</span></div>
                  <div className="text-[10px] text-[#64748B]">Evidence packages: <span className="text-[#F59E0B]">2 packages contained decisions from this window</span></div>
                </div>
              )}
              {queryResult === 'exemplary' && (
                <div className="mt-1 pt-1 border-t border-[#1E2A45] space-y-0.5">
                  <div className="text-[10px] text-[#10B981]">Governance status: ◆ EXEMPLARY — all dimensions meet or exceed threshold</div>
                  <div className="text-[10px] text-[#64748B]">Proof Chain grade: <span className="text-[#10B981]">◆ AUDIT-GRADE across all 5 layers</span></div>
                  <div className="text-[10px] text-[#64748B]">Oversight signals: <span className="text-[#94A3B8]">Average review: 6m 42s · Complexity-adjusted: within range</span></div>
                  <div className="mt-1 text-[9px] text-[#10B981]">These records form the strongest audit sample population.</div>
                </div>
              )}
              {queryResult === 'compound_full' && (
                <div className="mt-1 pt-1 border-t border-[#1E2A45] space-y-0.5">
                  <div className="text-[10px] text-[#64748B]">Dimensions queried: <span className="text-[#6366F1] font-semibold">Evidence coverage + Control State + Oversight signals</span></div>
                  <div className="text-[10px] text-[#64748B]">Time range: <span className="text-[#94A3B8]">April 10–15, 2026</span></div>
                  <div className="text-[10px] text-[#64748B]">Records matched: <span className="text-[#F8FAFC] font-mono">3 decisions meeting all three degraded conditions simultaneously</span></div>
                  <div className="text-[10px] text-[#64748B]">Control State: <span className="text-[#F59E0B]">CSS gap active during decision (Apr 15 · 11:14 UTC)</span></div>
                  <div className="text-[10px] text-[#64748B]">Evidence coverage: <span className="text-[#F59E0B]">71% during gap (below 80% threshold)</span></div>
                  <div className="text-[10px] text-[#64748B]">Oversight signals: <span className="text-[#F59E0B]">Review duration below complexity threshold</span></div>
                  <div className="text-[10px] text-[#64748B]">Proof Chain grade: <span className="text-[#F59E0B]">◆ OPERATIONS-GRADE — multiple factors degraded simultaneously</span></div>
                  <div className="text-[10px] text-[#64748B]">Compound governance failure window: <span className="text-[#EF4444] font-mono">2h 53m</span></div>
                  <div className="text-[10px] text-[#F59E0B]">Recommended action: Priority audit sample · Legal review of affected decisions</div>
                  <div className="mt-2 text-[9px] text-[#6366F1]">This query reconstructed governance state across 3 dimensions simultaneously at each decision timestamp. No other governance tool performs multi-dimensional temporal reconstruction.</div>
                </div>
              )}
              {queryResult === 'cssgap' && (
                <div className="mt-1 pt-1 border-t border-[#1E2A45] space-y-0.5">
                  <div className="text-[10px] text-[#64748B]">Control State gap window: <span className="text-[#F59E0B] font-mono">Apr 15, 2026 · 11:14 UTC → 14:31 UTC (3h 17m)</span></div>
                  <div className="text-[10px] text-[#64748B]">Governance coverage: <span className="text-[#F59E0B]">71% during gap · Below 80% threshold</span></div>
                  <div className="text-[10px] text-[#64748B]">Decisions in gap window: <span className="text-[#F8FAFC]">143 total · 4 HIGH-RISK</span></div>
                  <div className="text-[10px] text-[#64748B]">CSS root cause: <span className="text-[#F59E0B]">External Legal Corpus not on allowlist</span></div>
                  <div className="mt-2 p-3 rounded-lg bg-[#080D1A] border border-[#1E2A45]" data-testid="gap-window-viz">
                    <div className="text-[8px] font-mono text-[#475569] mb-1.5">GAP WINDOW VISUALIZATION · Apr 15, 2026</div>
                    <div className="relative h-4 bg-[#1E2A45] rounded-full overflow-hidden mb-1">
                      <div className="absolute top-0 bottom-0 bg-[#F59E0B]/30 rounded" style={{ left: '6%', width: '82%' }} />
                      <div className="absolute top-0 bottom-0 w-px bg-[#F59E0B]" style={{ left: '6%' }} />
                      <div className="absolute top-0 bottom-0 w-px bg-[#F59E0B]" style={{ left: '88%' }} />
                    </div>
                    <div className="flex justify-between text-[7px] font-mono text-[#475569]">
                      <span>11:00</span><span className="text-[#F59E0B]">11:14 Gap opened</span><span className="text-[#F59E0B]">14:31 Gap closed</span><span>15:00</span>
                    </div>
                    <div className="mt-1.5 space-y-0.5">
                      <div className="flex items-center gap-2 text-[8px]"><div className="h-2 bg-[#F59E0B]/40 rounded" style={{ width: '82%' }} /><span className="text-[#F59E0B] font-mono whitespace-nowrap">143 decisions</span></div>
                      <div className="flex items-center gap-2 text-[8px]"><div className="h-2 bg-[#EF4444]/40 rounded" style={{ width: '2.8%' }} /><span className="text-[#EF4444] font-mono whitespace-nowrap">4 HIGH-RISK</span></div>
                    </div>
                  </div>
                </div>
              )}
              {(queryResult === 'coverage' || queryResult === 'reviewer') && (
                <div className="mt-1 text-[10px] text-[#64748B]">
                  Time range: <span className="text-[#94A3B8]">{queryResult === 'coverage' ? 'April 15, 2026 · 11:14 UTC – 14:31 UTC' : 'April 15–17, 2026'}</span>
                  <br/>Records matched: <span className="text-[#F8FAFC] font-mono">{queryResult === 'coverage' ? '4' : '2'}</span>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Query Palette */}
        <div className="mt-3 space-y-1.5" data-testid="query-palette">
          <div className="text-[8px] font-semibold text-[#475569] uppercase tracking-wider">GOVERNANCE QUERY PALETTE · Proof Graph Service</div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <button onClick={() => setTemporalQuery('Show me every HIGH-RISK decision made while evidence coverage was below 80%')} className="text-[8px] text-[#6366F1] hover:text-[#818cf8] px-2 py-0.5 rounded bg-[#6366F1]/5">Coverage gaps</button>
            <button onClick={() => setTemporalQuery('Show decisions reviewed by LA-0912 in the 72 hours following the unauthorized change')} className="text-[8px] text-[#6366F1] hover:text-[#818cf8] px-2 py-0.5 rounded bg-[#6366F1]/5">Reviewer trace</button>
            <button onClick={() => setTemporalQuery('Show every HIGH-RISK decision executed while Control State had an active gap between Apr 12–15')} className="text-[8px] text-[#6366F1] hover:text-[#818cf8] px-2 py-0.5 rounded bg-[#6366F1]/5">CSS gap window</button>
            <button onClick={() => setTemporalQuery('Show every decision where oversight evidence was below defensibility threshold and reviewer lacked current AI literacy certification')} className="text-[8px] text-[#6366F1] hover:text-[#818cf8] px-2 py-0.5 rounded bg-[#6366F1]/5">Oversight + CSS</button>
            <button onClick={() => setTemporalQuery('Show every model version change that occurred without a corresponding Change Control record')} className="text-[8px] text-[#6366F1] hover:text-[#818cf8] px-2 py-0.5 rounded bg-[#6366F1]/5">Unauthorized changes</button>
            <button onClick={() => setTemporalQuery('Show all decisions with AUDIT-GRADE proof chain where human review duration exceeded output complexity baseline')} className="text-[8px] text-[#6366F1] hover:text-[#818cf8] px-2 py-0.5 rounded bg-[#6366F1]/5">Exemplary records</button>
            <div className="inline-flex flex-col items-start">
              <button onClick={() => setTemporalQuery('Show every HIGH-RISK decision executed while governance state was degraded and oversight evidence was below defensibility threshold between Apr 10–15')} className="text-[8px] text-[#6366F1] hover:text-[#818cf8] px-2 py-0.5 rounded border border-[#6366F1]/30 bg-[#6366F1]/10">Full compound failure ◆</button>
              <span className="text-[7px] text-[#6366F1]/70 mt-0.5">Multi-dimensional temporal reconstruction</span>
            </div>
          </div>
        </div>
        {/* Dynamic query history */}
        {queryHistory.length > 0 && (
          <div className="mt-1.5 space-y-0.5">
            {queryHistory.map((h, i) => (
              <div key={i} className="text-[8px] text-[#475569]">Recent: "{h.q.slice(0, 70)}..." · {h.count} results</div>
            ))}
          </div>
        )}
      </div>

      {/* Filter Bar */}
      <div className="flex items-center gap-3 flex-1">
          <div className="relative max-w-xs flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
            <input
              data-testid="ledger-search"
              type="text"
              placeholder="Search records, models..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-[#0F1629] border border-[#1E2A45] rounded-lg text-sm text-[#F8FAFC] placeholder:text-[#475569] focus:outline-none focus:border-[#6366F1] transition-colors"
            />
          </div>
          <select className="px-3 py-2 bg-[#0F1629] border border-[#1E2A45] rounded-lg text-sm text-[#94A3B8] focus:outline-none focus:border-[#6366F1]">
            <option>All Models</option>
            <option>GPT-4o</option>
            <option>Claude Sonnet</option>
            <option>Mistral 7B</option>
          </select>
          <select className="px-3 py-2 bg-[#0F1629] border border-[#1E2A45] rounded-lg text-sm text-[#94A3B8] focus:outline-none focus:border-[#6366F1]">
            <option>All Risk Tiers</option>
            <option>HIGH</option>
            <option>MEDIUM</option>
            <option>LOW</option>
          </select>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/dashboard/incident-review" data-testid="start-incident-review" className="flex items-center gap-1.5 text-xs text-[#EF4444] hover:text-[#F87171] font-medium px-3 py-2 border border-[#EF4444]/30 rounded-lg transition-colors">
            <Flame size={13} /> Start incident review
          </Link>
          <div className="text-xs text-[#64748B] font-mono">47,832 records · 30 days</div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="export-records-btn" className="flex items-center gap-2 bg-[#0F1629] border border-[#1E2A45] hover:border-[#6366F1] text-[#94A3B8] text-sm px-4 py-2 rounded-lg transition-colors">
                <Download size={14} /> Export <ChevronDown size={14} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#0F1629] border-[#1E2A45]">
              <DropdownMenuItem data-testid="export-csv" className="text-[#94A3B8] focus:bg-[#1E2A45] focus:text-[#F8FAFC] cursor-pointer text-xs">CSV Export</DropdownMenuItem>
              <DropdownMenuItem data-testid="export-pdf" className="text-[#94A3B8] focus:bg-[#1E2A45] focus:text-[#F8FAFC] cursor-pointer text-xs">PDF Report</DropdownMenuItem>
              <DropdownMenuItem data-testid="export-grc" className="text-[#94A3B8] focus:bg-[#1E2A45] focus:text-[#F8FAFC] cursor-pointer text-xs">GRC Integration (ServiceNow, Archer)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Ledger Table */}
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#1E2A45]">
                {['Record ID', 'Timestamp', 'Model', 'Workflow', 'Risk', 'Controls', 'Proof Chain', 'Sig'].map((h) => (
                  <th key={h} className="text-left text-[10px] font-medium text-[#475569] uppercase tracking-wider py-3 px-4">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((record) => (
                <React.Fragment key={record.id}>
                <tr
                  data-testid={`ledger-row-${record.id}`}
                  onClick={() => navigate(`/dashboard/decision-ledger/${record.id}`)}
                  className="border-b border-[#1E2A45]/50 table-row-interactive"
                >
                  <td className="py-3 px-4 font-mono text-[#6366F1] text-xs">{record.id}</td>
                  <td className="py-3 px-4 font-mono text-[#94A3B8] text-xs whitespace-nowrap">{record.timestamp}</td>
                  <td className="py-3 px-4 text-[#F8FAFC]">{record.model}</td>
                  <td className="py-3 px-4 text-[#94A3B8]">{record.workflow}</td>
                  <td className="py-3 px-4"><RiskBadge tier={record.riskTier} /></td>
                  <td className="py-3 px-4 font-mono text-[#94A3B8] text-xs">{record.controls}</td>
                  <td className="py-3 px-4" onClick={(e) => { e.stopPropagation(); setExpandedChain(expandedChain === record.id ? null : record.id); }}>
                    <GradeTooltip grade={record.isFailure ? 'OPERATIONS-GRADE' : 'AUDIT-GRADE'}>
                      <span className={`text-[10px] font-mono cursor-help ${
                        record.isFailure ? 'text-[#F59E0B]' :
                        record.sigStatus === 'unaudited' ? 'text-[#64748B]' :
                        'text-[#10B981]'
                      }`}>
                        {record.isFailure ? '◆ OPS-GRADE' : record.sigStatus === 'unaudited' ? '○ UNAUDITED' : '◆ AUDIT-GRADE'}
                      </span>
                    </GradeTooltip>
                  </td>
                  <td className="py-3 px-4"><SigIcon status={record.sigStatus} /></td>
                </tr>
                {expandedChain === record.id && (
                  <tr data-testid={`chain-dropdown-${record.id}`}>
                    <td colSpan={8} className="p-0">
                      <div className="px-5 py-4 bg-[#080D1A] border-l-[3px] border-l-[#6366F1]" style={{ animation: 'pageIn 0.15s ease-out' }}>
                        <div className="text-[10px] font-bold text-[#F8FAFC] uppercase tracking-wider mb-2">Proof Chain — {record.id}</div>
                        {record.isFailure ? (
                          <div className="space-y-1">
                            {[
                              { icon: '◆', color: '#10B981', text: 'Classification    CER-2026-001 · AUDIT-GRADE · HIGH-RISK · Deployer' },
                              { icon: '⚠', color: '#F59E0B', text: 'Control State    CSS-2026-031204 · AUDIT-GRADE · 71% coverage · CONTROL GAP' },
                              { icon: '⚠', color: '#F59E0B', text: 'Decision         DER-2026-031204 · OPERATIONS-GRADE · Integrity warning' },
                              { icon: '⚠', color: '#F59E0B', text: 'Oversight        OER-2026-031105 · OPERATIONS-GRADE · Weak signals' },
                              { icon: '⏸', color: '#475569', text: 'Package          PKG-2026-Q1-004 · BLOCKED · Awaiting CSS remediation' },
                            ].map((r, i) => (
                              <div key={i} className="flex items-center gap-2 text-[10px] font-mono">
                                <span style={{ color: r.color }}>{r.icon}</span>
                                <span className="text-[#94A3B8]">{r.text}</span>
                              </div>
                            ))}
                            <div className="text-[10px] text-[#F59E0B] mt-2">Weakest link: ◆ OPERATIONS-GRADE · Chain gap at Control State layer</div>
                            <div className="flex items-center gap-3 mt-2">
                              <button onClick={(e) => { e.stopPropagation(); navigate('/dashboard/proof-chain'); }} className="text-[10px] text-[#6366F1] hover:text-[#818cf8]">View Full Chain →</button>
                              <button onClick={(e) => { e.stopPropagation(); toast.info('Starting remediation'); }} className="text-[10px] text-[#F59E0B] hover:text-[#FBBF24]">Start Remediation →</button>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-1">
                            {[
                              { icon: '◆', text: `Classification    CER-2026-001 · AUDIT-GRADE · HIGH-RISK · Deployer` },
                              { icon: '◆', text: `Control State    CSS-2026-${record.id.split('-')[2]} · AUDIT-GRADE · 94% coverage · No gaps` },
                              { icon: '◆', text: `Decision         ${record.id} · AUDIT-GRADE · ${record.controls} controls · ${record.model}` },
                              { icon: '◆', text: `Oversight        OER-2026-${record.id.split('-')[2]} · AUDIT-GRADE · Defensible signals` },
                              { icon: '◆', text: `Package          PKG-2026-Q1-003 · AUDIT-GRADE · Complete` },
                            ].map((r, i) => (
                              <div key={i} className="flex items-center gap-2 text-[10px] font-mono">
                                <span className="text-[#10B981]">{r.icon}</span>
                                <span className="text-[#94A3B8]">{r.text}</span>
                              </div>
                            ))}
                            <div className="text-[10px] text-[#10B981] mt-2">All 5 layers: ◆ AUDIT-GRADE</div>
                            <button onClick={(e) => { e.stopPropagation(); navigate('/dashboard/proof-chain'); }} className="text-[10px] text-[#6366F1] hover:text-[#818cf8] mt-1">View Full Chain →</button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
